import { useId, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { sortSearchableOptions, type SearchableOption } from "@/lib/catalog-options";
export type { SearchableOption } from "@/lib/catalog-options";

/** Search only narrows supplied options; it never changes the stored selection or scope. */
export function SearchableSelector({
  label, value, options, onChange, placeholder = "Select…", disabled = false, size = "filter", id,
}: {
  label: string;
  value: string;
  options: readonly SearchableOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "filter" | "form";
  id?: string;
}): JSX.Element {
  const generatedId = useId();
  const listId = `${generatedId}-results`;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const words = search.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
  const results = sortSearchableOptions(options).filter((option) => {
    const text = [option.label, option.value, option.context, option.searchText].join(" ").toLocaleLowerCase();
    return words.every((word) => text.includes(word));
  });
  const enabled = results.filter((option) => !option.disabled);
  const activeOption = enabled.find((option) => option.value === active);
  const selected = options.find((option) => option.value === value);
  function choose(option: SearchableOption): void {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
  }
  return (
    <Popover.Root open={open} onOpenChange={(next) => {
      setOpen(next);
      if (next) { setSearch(""); setActive(null); }
    }}>
      <Popover.Trigger asChild>
        <button id={id} type="button" aria-label={label} disabled={disabled}
          className={cn("box-border flex w-full min-w-0 max-w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50", size === "form" ? "h-10" : "h-9")}
          title={selected?.label ?? (value ? `${value} (Unavailable)` : placeholder)}>
          <span className="min-w-0 truncate">{selected?.label ?? (value ? `${value} (Unavailable)` : placeholder)}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={4} collisionPadding={12}
          className="z-50 flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] min-w-0 max-w-[calc(100vw-1.5rem)] flex-col rounded-md border border-input bg-popover p-2 text-popover-foreground shadow-md"
          onOpenAutoFocus={(event) => { event.preventDefault(); input.current?.focus(); }}>
          <Input ref={input} role="combobox" aria-label={`Search ${label}`} aria-autocomplete="list"
            aria-expanded={open} aria-controls={listId}
            aria-activedescendant={activeOption ? `${listId}-${results.indexOf(activeOption)}` : undefined}
            className="h-9 shrink-0" value={search} placeholder="Search…"
            onChange={(event) => { setSearch(event.target.value); setActive(null); }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const index = enabled.findIndex((option) => option.value === active);
                const next = index < 0 ? (event.key === "ArrowDown" ? 0 : enabled.length - 1)
                  : (index + (event.key === "ArrowDown" ? 1 : -1) + enabled.length) % enabled.length;
                const option = enabled[next];
                if (option) {
                  setActive(option.value);
                  document.getElementById(`${listId}-${results.indexOf(option)}`)?.scrollIntoView?.({ block: "nearest" });
                }
              } else if (event.key === "Enter") {
                event.preventDefault();
                if (activeOption) choose(activeOption);
              }
            }} />
          <div id={listId} role="listbox" aria-label={label}
            className="mt-1 min-h-0 max-h-64 overflow-y-auto overscroll-contain">
            {results.map((option, index) => (
              <div key={option.value} id={`${listId}-${index}`} role="option"
                aria-selected={option.value === value} aria-disabled={option.disabled || undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(option)}
                className={cn("flex min-w-0 cursor-default gap-2 rounded-sm px-2 py-2 text-sm [overflow-wrap:anywhere]", option.disabled ? "opacity-50" : "cursor-pointer hover:bg-accent", active === option.value && "bg-accent")}>
                <Check className={cn("mt-0.5 h-4 w-4 shrink-0", option.value !== value && "invisible")} />
                <span className="min-w-0 whitespace-normal">{option.label}{option.context ? <span className="block text-xs text-muted-foreground">{option.context}</span> : null}</span>
              </div>
            ))}
            {results.length === 0 ? <p role="status" className="p-3 text-sm text-muted-foreground">No matches.</p> : null}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
